"use client";

import { useEffect, useRef } from "react";
import { motion } from "framer-motion";

const WA_MESSAGE = encodeURIComponent(
  "Hi, I'm interested in Scaled-Out infrastructure. My monthly ad spend is approximately $____. I operate in ______ market. Looking to scale without spending limits."
);
const WA_URL = `https://wa.me/447700000000?text=${WA_MESSAGE}`;

export default function ContactContent() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    ref?.current?.querySelectorAll(".reveal")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      {/* Hero */}
      <section className="relative pt-40 pb-24 bg-[#0B0F14] overflow-hidden">
        <div className="animated-grid absolute inset-0 z-0 opacity-40" />
        <div
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 50% 60% at 50% 0%, rgba(0,230,118,0.06) 0%, transparent 70%)",
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="tag-badge mx-auto mb-6 inline-flex reveal">Contact</div>
          <h1 className="hero-title text-[#F0F4F8] mb-6 reveal reveal-delay-1">
            Initiate Your{" "}
            <span className="text-[#00E676]">Infrastructure Review.</span>
          </h1>
          <p className="text-[#6B7A8D] text-lg max-w-xl mx-auto leading-relaxed reveal reveal-delay-2">
            All onboarding begins with a review. Tell us about your current
            spend and target markets. We will assess infrastructure fit and
            respond directly.
          </p>
        </div>
      </section>
      {/* WhatsApp CTA */}
      <section className="py-24 bg-[#11161D]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left */}
            <div className="reveal">
              <h2 className="text-2xl font-bold text-[#F0F4F8] mb-6">
                Start On <span className="text-[#00E676]">WhatsApp</span>
              </h2>
              <p className="text-[#6B7A8D] text-sm leading-relaxed mb-8">
                Our primary onboarding channel is WhatsApp. Click the button
                below to open a pre-filled message. Complete the details about
                your monthly spend and market, then send. We review all
                applications within 24 hours.
              </p>

              {/* Message Preview */}
              <div className="card-neon rounded-xl p-5 mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <span className="status-dot w-1.5 h-1.5 rounded-full bg-[#25D366]" />
                  <span className="text-[#6B7A8D] font-mono text-[10px] tracking-[0.1em] uppercase">
                    Pre-filled Message Template
                  </span>
                </div>
                <p className="text-[#F0F4F8] text-sm leading-relaxed font-mono">
                  Hi, I&apos;m interested in Scaled-Out infrastructure. My monthly
                  ad spend is approximately{" "}
                  <span className="text-[#00E676]">$____</span>. I operate in{" "}
                  <span className="text-[#00E676]">______</span> market.
                  Looking to scale without spending limits.
                </p>
              </div>

              <a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-solid-glow px-10 py-4 rounded-full text-sm font-bold uppercase tracking-[0.1em] inline-flex items-center gap-3"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-5 h-5"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Open WhatsApp →
              </a>
            </div>

            {/* Right — Process */}
            <div className="reveal reveal-delay-2">
              <h3 className="text-[#F0F4F8] font-bold text-base mb-6 uppercase tracking-[0.1em]">
                Onboarding Process
              </h3>
              <div className="space-y-4">
                {[
                  { step: "01", title: "Send Initial Message", desc: "Complete the pre-filled WhatsApp template with your spend and market details." },
                  { step: "02", title: "Infrastructure Review", desc: "Our team reviews your application against current capacity and operational fit." },
                  { step: "03", title: "Activation Confirmation", desc: "Approved applicants receive infrastructure access within 1 business day." },
                  { step: "04", title: "Capital Deployment", desc: "Begin top-ups and deploy capital across your Meta campaigns immediately." },
                ]?.map((item, i) => (
                  <div key={item?.step} className="flex gap-4">
                    <div
                      className="w-8 h-8 rounded-full border border-[rgba(0,230,118,0.3)] flex items-center justify-center flex-shrink-0 mt-0.5"
                    >
                      <span className="text-[#00E676] font-mono text-[10px] font-bold">
                        {item?.step}
                      </span>
                    </div>
                    <div>
                      <h4 className="text-[#F0F4F8] font-bold text-sm mb-1">{item?.title}</h4>
                      <p className="text-[#6B7A8D] text-xs leading-relaxed">{item?.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Info Strip */}
      <section className="py-16 bg-[#0B0F14]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-4 reveal">
            {[
              { label: "Response Time", val: "< 24 hours" },
              { label: "Onboarding Speed", val: "1 Business Day" },
              { label: "Support Hours", val: "24/7" },
            ]?.map((item) => (
              <div key={item?.label} className="card-neon rounded-xl p-6 text-center">
                <div className="text-[#00E676] font-mono font-bold text-lg mb-1">{item?.val}</div>
                <div className="text-[#6B7A8D] text-xs uppercase tracking-[0.12em] font-bold">{item?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </motion.div>
  );
}